import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Select,
    MenuItem,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    useTheme,
    TablePagination,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Popover,
    FormControl,
    InputLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    CircularProgress,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import PublishIcon from '@mui/icons-material/Publish';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import SecurityIcon from '@mui/icons-material/Security';
import DescriptionIcon from '@mui/icons-material/Description';
import { collectionPointsService, CollectionPoint } from '../../services/collectionpoints-service';
import AddCollectionPanel from './AddCollectionPanel';
import EditCollectionPointPanel from './EditCollectionPointPanel';
import NoticeDesigner from './NoticeDesigner';
import { Global } from '@emotion/react';

const CollectionPoints: React.FC = () => {
    const theme = useTheme();
    const [sortBy, setSortBy] = useState('newest');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [collectionPoints, setCollectionPoints] = useState<CollectionPoint[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [undeleteConfirmOpen, setUndeleteConfirmOpen] = useState(false);
    const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
    const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
    const [selectedCollectionPoint, setSelectedCollectionPoint] = useState<CollectionPoint | null>(null);
    const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
    const [collectionPointToDelete, setCollectionPointToDelete] = useState<CollectionPoint | null>(null);
    const [collectionPointToUndelete, setCollectionPointToUndelete] = useState<CollectionPoint | null>(null);
    const [collectionPointToPublish, setCollectionPointToPublish] = useState<CollectionPoint | null>(null);
    const [totalCount, setTotalCount] = useState(0);
    const [isNoticeDesignerOpen, setIsNoticeDesignerOpen] = useState(false);
    const [selectedNoticeCollectionPoint, setSelectedNoticeCollectionPoint] = useState<CollectionPoint | null>(null);

    // Filter states
    const [filterAnchorEl, setFilterAnchorEl] = useState<null | HTMLElement>(null);
    const [filters, setFilters] = useState({
        isPublished: 'ALL',
        trackGeolocation: 'ALL',
        type: 'ALL'
    });
    const [appliedFilters, setAppliedFilters] = useState({
        isPublished: 'ALL',
        trackGeolocation: 'ALL',
        type: 'ALL'
    });
    const [filterLoading, setFilterLoading] = useState(false);

    // Helper function to get abbreviated collection point type
    const getAbbreviatedType = (type: string) => {
        switch (type) {
            case 'HOSTED_WEBFORM': return 'HW';
            case 'EMBEDDED_WEBFORM': return 'EW';
            case 'BULK_UPLOAD': return 'BU';
            case 'NOTICE': return 'N';
            default: return type;
        }
    };

    // Helper function to get full type name for tooltip
    const getFullTypeName = (type: string) => {
        switch (type) {
            case 'HOSTED_WEBFORM': return 'Hosted Webform';
            case 'EMBEDDED_WEBFORM': return 'Embedded Webform';
            case 'BULK_UPLOAD': return 'Bulk Upload';
            case 'NOTICE': return 'Notice';
            default: return type;
        }
    };

    // Helper function to get type description
    const getTypeDescription = (type: string, isDPPortal = false) => {
        switch (type) {
            case 'API':
                return 'Privigo provides API endpoints for consent management. Clients create and host their own forms, then submit consent records through the API.';
            case 'HOSTED_WEBFORM':
                return 'Use Privigo form designer to create forms hosted on our platform. Forms can be displayed on client websites using iframe integration.';
            case 'EMBEDDED_WEBFORM':
                return 'Forms created with Privigo designer are embedded directly into client websites using JavaScript integration.';
            case 'NOTICE':
                if (isDPPortal) {
                    return 'System collection point for audit trail purposes. Records consent changes made by data principals through the DP Portal. This is not a configurable notice form.';
                } else {
                    return 'For existing data principals with client relationships. Create notices to request additional information or consent, accessible through the DP portal.';
                }
            case 'BULK_UPLOAD':
                return 'Upload existing PII data collected offline (e.g., paper forms) via Excel sheets. Data principals receive email notices to provide consent.';
            default:
                return '';
        }
    };

    // Helper function to get type tooltip content (name + description)
    const getTypeTooltipContent = (collectionPoint: CollectionPoint) => {
        const type = collectionPoint.CollectionPointType;
        const name = getFullTypeName(type);
        const isDPPortalCP = isDPPortalCollectionPoint(collectionPoint);
        const description = getTypeDescription(type, isDPPortalCP);
        if (description) {
            return (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {name}
                    </Typography>
                    <Typography variant="caption">
                        {description}
                    </Typography>
                </Box>
            );
        }
        return name;
    };

    // Helper function to truncate text
    const truncateText = (text: string, maxLength = 20) => {
        if (!text) return '';
        return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
    };

    // Helper function to check if collection point is DP_PORTAL system collection point
    const isDPPortalCollectionPoint = (collectionPoint: CollectionPoint): boolean => {
        return collectionPoint.CollectionPointIdentifier?.startsWith('DP_PORTAL_') || false;
    };

    // Helper function to check if collection point is eligible for Notice Designer
    const isEligibleForNoticeDesigner = (collectionPoint: CollectionPoint): boolean => {
        return collectionPoint.CollectionPointType === 'NOTICE' 
            && !isDPPortalCollectionPoint(collectionPoint)
            && collectionPoint.IsPublished === true;
    };

    // Handler to open Notice Designer
    const handleOpenNoticeDesigner = (collectionPoint: CollectionPoint) => {
        setSelectedNoticeCollectionPoint(collectionPoint);
        setIsNoticeDesignerOpen(true);
    };

    useEffect(() => {
        loadCollectionPoints();
    }, [page, rowsPerPage, sortBy, appliedFilters]);

    const loadCollectionPoints = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const queryParams = {
                filters: {
                    isPublished: appliedFilters.isPublished === 'ALL' ? undefined : appliedFilters.isPublished === 'PUBLISHED',
                    trackGeolocation: appliedFilters.trackGeolocation === 'ALL' ? undefined : appliedFilters.trackGeolocation === 'YES',
                    type: appliedFilters.type === 'ALL' ? undefined : appliedFilters.type as any
                },
                pagination: {
                    page: page + 1,
                    pageSize: rowsPerPage
                },
                sortBy: sortBy as 'A-Z' | 'Z-A' | 'newest' | 'oldest' | 'Newest' | 'Oldest' | 'a-z' | 'z-a'
            };
            
            const response = await collectionPointsService.getCollectionPoints(queryParams);
            setCollectionPoints(response.data);
            setTotalCount(response.pagination.total);
        } catch (error) {
            console.error('Error loading collection points:', error);
            setError(error instanceof Error ? error.message : 'Failed to fetch collection points');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newRowsPerPage = parseInt(event.target.value, 10);
        setRowsPerPage(newRowsPerPage);
        setPage(0); // Reset to first page when changing rows per page
    };

    const handleSortChange = (newSortBy: string) => {
        setSortBy(newSortBy);
        setPage(0); // Reset to first page when changing sort
    };

    const handleDelete = async (collectionPoint: CollectionPoint) => {
        setCollectionPointToDelete(collectionPoint);
        setDeleteConfirmOpen(true);
    };

    const handleUndelete = async (collectionPoint: CollectionPoint) => {
        setCollectionPointToUndelete(collectionPoint);
        setUndeleteConfirmOpen(true);
    };

    const handlePublish = async (collectionPoint: CollectionPoint) => {
        setCollectionPointToPublish(collectionPoint);
        setPublishConfirmOpen(true);
    };

    const handleEdit = (collectionPoint: CollectionPoint) => {
        setSelectedCollectionPoint(collectionPoint);
        setIsEditPanelOpen(true);
    };

    const confirmDelete = async () => {
        if (collectionPointToDelete) {
            try {
                await collectionPointsService.deleteCollectionPoint(collectionPointToDelete.CollectionPointId);
                await loadCollectionPoints();
                setDeleteConfirmOpen(false);
                setCollectionPointToDelete(null);
            } catch (error) {
                console.error('Error deleting collection point:', error);
            }
        }
    };

    const confirmUndelete = async () => {
        if (collectionPointToUndelete) {
            try {
                await collectionPointsService.undeleteCollectionPoint(collectionPointToUndelete.CollectionPointId);
                await loadCollectionPoints();
                setUndeleteConfirmOpen(false);
                setCollectionPointToUndelete(null);
            } catch (error) {
                console.error('Error undeleting collection point:', error);
            }
        }
    };

    const confirmPublish = async () => {
        if (collectionPointToPublish) {
            try {
                await collectionPointsService.publishCollectionPoint(collectionPointToPublish.CollectionPointId);
                await loadCollectionPoints();
                setPublishConfirmOpen(false);
                setCollectionPointToPublish(null);
            } catch (error) {
                console.error('Error publishing collection point:', error);
            }
        }
    };

    // Filter handlers
    const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setFilterAnchorEl(event.currentTarget);
        // Reset the filters in the popover to match the currently applied filters
        setFilters(appliedFilters);
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    const handleFilterChange = (field: keyof typeof filters, value: string) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApplyFilters = async () => {
        try {
            setFilterLoading(true);
            setAppliedFilters(filters); // Apply the current filter values
            setPage(0); // Reset to first page when applying filters
            // The loadCollectionPoints will be called by the useEffect when appliedFilters change
        } catch (error) {
            console.error('Error applying filters:', error);
        } finally {
            setFilterLoading(false);
            handleFilterClose();
        }
    };

    const handleClearFilters = () => {
        // Only reset the filters in the popup, don't apply them immediately
        const clearedFilters = {
            isPublished: 'ALL',
            trackGeolocation: 'ALL',
            type: 'ALL'
        };
        setFilters(clearedFilters);
    };

    const hasActiveFilters = () => {
        return appliedFilters.isPublished !== 'ALL' || 
               appliedFilters.trackGeolocation !== 'ALL' || 
               appliedFilters.type !== 'ALL';
    };

    const hasUnsavedFilterChanges = () => {
        return filters.isPublished !== appliedFilters.isPublished ||
               filters.trackGeolocation !== appliedFilters.trackGeolocation ||
               filters.type !== appliedFilters.type;
    };

    if (loading) {
        return (
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: theme.palette.text.primary
            }}>
                Loading collection points...
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
        }}>
            {/* Header */}
            <Box sx={{ 
                px: 3, 
                pt: 2,
                pb: 3,
                bgcolor: theme.palette.background.default 
            }}>
                <Typography variant="h4" sx={{
                    color: theme.palette.text.primary,
                    mb: 3
                }}>
                    Data Principal Entry Points
                </Typography>

                {/* Controls */}
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    gap: 2,
                    flexWrap: 'wrap'
                }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        {/* Left side controls can be added here if needed */}
                    </Box>
                    <Box sx={{ 
                        display: 'flex', 
                        gap: 2, 
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        ml: 'auto'
                    }}>
                        <Global styles={`
                            @keyframes spin {
                                100% { transform: rotate(360deg); }
                            }
                        `} />
                        <IconButton
                            onClick={loadCollectionPoints}
                            sx={{ color: theme.palette.text.primary }}
                            disabled={loading}
                        >
                            <RefreshIcon
                                sx={{
                                    transition: 'transform 0.3s',
                                    animation: loading ? 'spin 1s linear infinite' : undefined
                                }}
                            />
                        </IconButton>
                        <Select
                            value={sortBy}
                            onChange={(e) => handleSortChange(e.target.value)}
                            size="small"
                            sx={{ 
                                width: 150,
                                bgcolor: theme.palette.background.paper,
                                color: theme.palette.text.primary,
                                '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.divider,
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: theme.palette.primary.main,
                                },
                                '& .MuiSelect-icon': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            <MenuItem value="A-Z" sx={{ color: theme.palette.text.primary }}>A - Z</MenuItem>
                            <MenuItem value="Z-A" sx={{ color: theme.palette.text.primary }}>Z - A</MenuItem>
                            <MenuItem value="newest" sx={{ color: theme.palette.text.primary }}>Newest</MenuItem>
                            <MenuItem value="oldest" sx={{ color: theme.palette.text.primary }}>Oldest</MenuItem>
                        </Select>
                        <IconButton 
                            onClick={handleFilterClick}
                            sx={{ 
                                color: (hasActiveFilters() || hasUnsavedFilterChanges()) ? theme.palette.primary.main : theme.palette.text.primary,
                                '&:hover': {
                                    bgcolor: theme.palette.action.hover,
                                },
                                position: 'relative',
                                '&::after': (hasActiveFilters() || hasUnsavedFilterChanges()) ? {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    bgcolor: theme.palette.primary.main,
                                    border: `2px solid ${theme.palette.background.paper}`,
                                } : {}
                            }}
                        >
                            <FilterListIcon />
                        </IconButton>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setIsAddPanelOpen(true)}
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                color: theme.palette.primary.contrastText,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.dark
                                }
                            }}
                        >
                            ADD NEW
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* Filter Popover */}
            <Popover
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={handleFilterClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        mt: 1,
                        width: 320,
                        bgcolor: theme.palette.background.paper,
                        boxShadow: theme.shadows[8],
                    },
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        Filters
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                            Published Status
                        </Typography>
                        <RadioGroup
                            value={filters.isPublished}
                            onChange={(e) => handleFilterChange('isPublished', e.target.value)}
                            row
                        >
                            <FormControlLabel 
                                value="ALL" 
                                control={<Radio size="small" sx={{ color: theme.palette.text.secondary }} />} 
                                label="All" 
                                sx={{ color: theme.palette.text.primary }}
                            />
                            <FormControlLabel 
                                value="PUBLISHED" 
                                control={<Radio size="small" sx={{ color: theme.palette.text.secondary }} />} 
                                label="Published" 
                                sx={{ color: theme.palette.text.primary }}
                            />
                            <FormControlLabel 
                                value="DRAFT" 
                                control={<Radio size="small" sx={{ color: theme.palette.text.secondary }} />} 
                                label="Draft" 
                                sx={{ color: theme.palette.text.primary }}
                            />
                        </RadioGroup>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                            Track Geolocation
                        </Typography>
                        <RadioGroup
                            value={filters.trackGeolocation}
                            onChange={(e) => handleFilterChange('trackGeolocation', e.target.value)}
                            row
                        >
                            <FormControlLabel 
                                value="ALL" 
                                control={<Radio size="small" sx={{ color: theme.palette.text.secondary }} />} 
                                label="All" 
                                sx={{ color: theme.palette.text.primary }}
                            />
                            <FormControlLabel 
                                value="YES" 
                                control={<Radio size="small" sx={{ color: theme.palette.text.secondary }} />} 
                                label="Yes" 
                                sx={{ color: theme.palette.text.primary }}
                            />
                            <FormControlLabel 
                                value="NO" 
                                control={<Radio size="small" sx={{ color: theme.palette.text.secondary }} />} 
                                label="No" 
                                sx={{ color: theme.palette.text.primary }}
                            />
                        </RadioGroup>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary }}>
                            Type
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={filters.type}
                                onChange={(e) => handleFilterChange('type', e.target.value)}
                                sx={{
                                    color: theme.palette.text.primary,
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.divider,
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: theme.palette.primary.main,
                                    },
                                    '& .MuiSelect-icon': {
                                        color: theme.palette.text.primary,
                                    }
                                }}
                            >
                                <MenuItem value="ALL" sx={{ color: theme.palette.text.primary }}>All Types</MenuItem>
                                <MenuItem value="API" sx={{ color: theme.palette.text.primary }}>API</MenuItem>
                                <MenuItem value="HOSTED_WEBFORM" sx={{ color: theme.palette.text.primary }}>Hosted Webform</MenuItem>
                                <MenuItem value="EMBEDDED_WEBFORM" sx={{ color: theme.palette.text.primary }}>Embedded Webform</MenuItem>
                                <MenuItem value="NOTICE" sx={{ color: theme.palette.text.primary }}>Notice</MenuItem>
                                <MenuItem value="BULK_UPLOAD" sx={{ color: theme.palette.text.primary }}>Bulk Upload</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button
                            onClick={handleClearFilters}
                            variant="outlined"
                            size="small"
                            sx={{ 
                                color: theme.palette.primary.main,
                                borderColor: theme.palette.primary.main,
                                '&:hover': {
                                    bgcolor: theme.palette.primary.main + '10',
                                    borderColor: theme.palette.primary.dark,
                                },
                                '&:disabled': {
                                    color: theme.palette.text.disabled,
                                    borderColor: theme.palette.text.disabled,
                                }
                            }}
                        >
                            Clear All
                        </Button>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                                onClick={handleFilterClose}
                                disabled={filterLoading}
                                sx={{ 
                                    color: theme.palette.text.primary,
                                    '&:hover': {
                                        bgcolor: theme.palette.action.hover,
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleApplyFilters}
                                disabled={filterLoading || !hasUnsavedFilterChanges()}
                                sx={{ 
                                    bgcolor: hasUnsavedFilterChanges() ? theme.palette.primary.main : theme.palette.action.disabled,
                                    color: theme.palette.primary.contrastText,
                                    '&:hover': { 
                                        bgcolor: hasUnsavedFilterChanges() ? theme.palette.primary.dark : theme.palette.action.disabled
                                    },
                                    minWidth: 100,
                                    position: 'relative'
                                }}
                            >
                                {filterLoading ? (
                                    <CircularProgress
                                        size={24}
                                        sx={{
                                            color: theme.palette.primary.contrastText,
                                            position: 'absolute',
                                            top: '50%',
                                            left: '50%',
                                            marginTop: '-12px',
                                            marginLeft: '-12px',
                                        }}
                                    />
                                ) : (
                                    'Apply'
                                )}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Popover>

            {/* Table Section */}
            <Box sx={{ 
                flex: 1,
                mx: 3,
                mb: 3,
                overflow: 'hidden',
                bgcolor: theme.palette.background.paper,
                borderRadius: 1
            }}>
                <TableContainer sx={{ 
                    height: '100%',
                    overflowX: 'auto',
                    '&::-webkit-scrollbar': {
                        height: 8,
                        width: 8
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
                        borderRadius: 4
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[400],
                        borderRadius: 4,
                        '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[500] : theme.palette.grey[300]
                        }
                    }
                }}>
                    <Table sx={{ minWidth: 1200 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500, width: '25%' }}>Name</TableCell>
                                <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500, width: '15%' }}>Department</TableCell>
                                <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500, width: '7%' }}>Type</TableCell>
                                <TableCell sx={{ width: '32px', minWidth: '32px', maxWidth: '32px', padding: '8px 4px' }}></TableCell>
                                <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500, width: '10%' }}>Published</TableCell>
                                <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500, width: '20%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        Identifier
                                        <Tooltip title="The data element thats used to uniquely identify a data principal created via this Entry Point">
                                            <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500, width: '8%', textAlign: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                        Track Geolocation
                                        <Tooltip title="The collection point tracks the location from where the DP interacted with the collection point">
                                            <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500, width: '8%', textAlign: 'center' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}>
                                        Minor Support
                                        <Tooltip title="This Entry Point accepts consent by minors as long as they are provided by parent or guardian">
                                            <InfoIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ color: theme.palette.text.primary, fontWeight: 500, width: '12%' }}>Created Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {collectionPoints.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={9} sx={{ borderBottom: 'none' }}>
                                        <Box sx={{ 
                                            display: 'flex', 
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            py: 8
                                        }}>
                                            <Typography 
                                                variant="h6" 
                                                sx={{ 
                                                    mb: 3,
                                                    color: theme.palette.text.secondary,
                                                    textAlign: 'center'
                                                }}
                                            >
                                                There are no collection points to display. Click the button below to create your first collection point
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={() => setIsAddPanelOpen(true)}
                                                sx={{ 
                                                    bgcolor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    '&:hover': { 
                                                        bgcolor: theme.palette.primary.dark 
                                                    }
                                                }}
                                            >
                                                ADD NEW
                                            </Button>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                collectionPoints.map((collectionPoint) => (
                                        <TableRow
                                            key={collectionPoint.CollectionPointId}
                                            hover
                                        >
                                            <TableCell 
                                                sx={{ 
                                                    cursor: 'pointer',
                                                    color: theme.palette.text.primary,
                                                    '&:hover': {
                                                        color: theme.palette.primary.main
                                                    }
                                                }}
                                                onClick={() => handleEdit(collectionPoint)}
                                            >
                                                {collectionPoint.Name}
                                            </TableCell>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                {collectionPoint.DepartmentName || `Department ${collectionPoint.DepartmentId}`}
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip 
                                                    title={getTypeTooltipContent(collectionPoint)} 
                                                    arrow
                                                    placement="top"
                                                    componentsProps={{
                                                        tooltip: {
                                                            sx: {
                                                                maxWidth: 400,
                                                                bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                                                '& .MuiTooltip-arrow': {
                                                    color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[900],
                                                },
                                            },
                                        },
                                    }}
                                                >
                                                    <Chip
                                                        label={getAbbreviatedType(collectionPoint.CollectionPointType)}
                                                        color="primary"
                                                        size="small"
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center', width: '32px', minWidth: '32px', maxWidth: '32px', padding: '8px 4px' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    {/* System icon for DP_PORTAL collection points */}
                                                    {isDPPortalCollectionPoint(collectionPoint) ? (
                                                        <Tooltip title="System collection point for audit trail - cannot be configured">
                                                            <SecurityIcon 
                                                                sx={{ 
                                                                    fontSize: 18,
                                                                    color: theme.palette.text.secondary,
                                                                    opacity: 0.7
                                                                }} 
                                                            />
                                                        </Tooltip>
                                                    ) : collectionPoint.CollectionPointType === 'NOTICE' ? (
                                                        /* Design Notice button for NOTICE collection points */
                                                        isEligibleForNoticeDesigner(collectionPoint) ? (
                                                            <Tooltip title="Design Notice Form">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleOpenNoticeDesigner(collectionPoint);
                                                                    }}
                                                                    sx={{
                                                                        color: theme.palette.primary.main,
                                                                        padding: '2px',
                                                                        '&:hover': {
                                                                            bgcolor: theme.palette.primary.main + '15'
                                                                        }
                                                                    }}
                                                                >
                                                                    <DesignServicesIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title="Notice collection point (not published)">
                                                                <DescriptionIcon 
                                                                    sx={{ 
                                                                        fontSize: 18,
                                                                        color: theme.palette.text.secondary,
                                                                        opacity: 0.5
                                                                    }} 
                                                                />
                                                            </Tooltip>
                                                        )
                                                    ) : (
                                                        /* User-defined icon for other collection point types */
                                                        <Tooltip title="User-defined collection point">
                                                            <DescriptionIcon 
                                                                sx={{ 
                                                                    fontSize: 18,
                                                                    color: theme.palette.text.secondary,
                                                                    opacity: 0.5
                                                                }} 
                                                            />
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={collectionPoint.IsPublished ? 'Published' : 'Draft'}
                                                    color={collectionPoint.IsPublished ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                <Tooltip 
                                                    title={
                                                        collectionPoint.IdentifierElementName 
                                                            ? (
                                                                <div>
                                                                    <div><strong>{collectionPoint.IdentifierElementName}</strong></div>
                                                                    {collectionPoint.IdentifierElementDescription && (
                                                                        <div>{collectionPoint.IdentifierElementDescription}</div>
                                                                    )}
                                                                    {collectionPoint.IdentifierElementCode && (
                                                                        <div><em>Code: {collectionPoint.IdentifierElementCode}</em></div>
                                                                    )}
                                                                </div>
                                                            )
                                                            : `ID: ${collectionPoint.IdentifierDataElementId}`
                                                    }
                                                    arrow
                                                >
                                                    <Chip
                                                        label={truncateText(collectionPoint.IdentifierElementName || `ID: ${collectionPoint.IdentifierDataElementId}`)}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {collectionPoint.TrackGeolocation ? (
                                                    <CheckCircleIcon sx={{ color: theme.palette.success.main }} />
                                                ) : (
                                                    <CancelIcon sx={{ color: theme.palette.text.disabled }} />
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>
                                                {collectionPoint.EnableParentChildRelationship ? (
                                                    <Tooltip title="Supports minor consent with parent/guardian approval">
                                                        <PersonIcon 
                                                            sx={{ 
                                                                color: theme.palette.success.main,
                                                                fontSize: 20
                                                            }} 
                                                        />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Does not support minor consent">
                                                        <PersonIcon 
                                                            sx={{ 
                                                                color: theme.palette.text.disabled,
                                                                fontSize: 20
                                                            }} 
                                                        />
                                                    </Tooltip>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                {collectionPoint.CreatedDate ? 
                                                    new Date(collectionPoint.CreatedDate).toLocaleDateString('en-US', { 
                                                        year: 'numeric', 
                                                        month: 'short', 
                                                        day: 'numeric',
                                                        timeZone: 'UTC'
                                                    }) 
                                                    : ''
                                                }
                                            </TableCell>
                                        </TableRow>
                                    ))
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={totalCount}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        sx={{
                            color: theme.palette.text.primary,
                            '& .MuiTablePagination-selectIcon': {
                                color: theme.palette.text.primary,
                            },
                        }}
                    />
                </TableContainer>
            </Box>

            <Dialog
                open={deleteConfirmOpen}
                onClose={() => setDeleteConfirmOpen(false)}
                sx={{
                    '& .MuiDialog-paper': {
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                    },
                }}
            >
                <DialogTitle sx={{ color: theme.palette.text.primary }}>Delete Collection Point</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: theme.palette.text.secondary }}>
                        Are you sure you want to delete this collection point?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={confirmDelete} color="primary" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={undeleteConfirmOpen}
                onClose={() => setUndeleteConfirmOpen(false)}
                sx={{
                    '& .MuiDialog-paper': {
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                    },
                }}
            >
                <DialogTitle sx={{ color: theme.palette.text.primary }}>Undelete Collection Point</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: theme.palette.text.secondary }}>
                        Are you sure you want to restore this collection point?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUndeleteConfirmOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={confirmUndelete} color="primary" variant="contained">Undelete</Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={publishConfirmOpen}
                onClose={() => setPublishConfirmOpen(false)}
                sx={{
                    '& .MuiDialog-paper': {
                        bgcolor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                    },
                }}
            >
                <DialogTitle sx={{ color: theme.palette.text.primary }}>Publish Collection Point</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ color: theme.palette.text.secondary }}>
                        Are you sure you want to publish this collection point?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPublishConfirmOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={confirmPublish} color="primary" variant="contained">Publish</Button>
                </DialogActions>
            </Dialog>

            <AddCollectionPanel 
                open={isAddPanelOpen}
                onClose={() => setIsAddPanelOpen(false)}
                onSave={loadCollectionPoints}
            />

            <EditCollectionPointPanel
                open={isEditPanelOpen}
                onClose={() => {
                    setIsEditPanelOpen(false);
                    setSelectedCollectionPoint(null);
                }}
                collectionPoint={selectedCollectionPoint}
                onSaved={loadCollectionPoints}
            />

            <NoticeDesigner
                open={isNoticeDesignerOpen}
                onClose={() => {
                    setIsNoticeDesignerOpen(false);
                    setSelectedNoticeCollectionPoint(null);
                }}
                collectionPoint={selectedNoticeCollectionPoint}
                onSaved={loadCollectionPoints}
            />
        </Box>
    );
};

export default CollectionPoints; 